import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { PageContainer } from '@/components/layout/PageContainer'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Loader2,
  Settings,
  Upload,
  X,
  AlertCircle,
  Type,
  Image as ImageIcon,
  Users,
  ArrowLeft,
  Smile,
  CalendarClock,
  Trash2,
  Zap,
  Sparkles,
  Film,
  Play,
} from 'lucide-react'
import {
  RichEditor,
  VideoEditor,
  type RichState,
  type VideoState,
} from './BroadcastRichVideoEditor'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useLineMessagingConfig } from '@/features/storefront/settings/useLineMessagingConfig'
import { useLineOADashboard } from './useLineOADashboard'
import {
  useLineOABroadcastQuota,
  useSendBroadcast,
  useScheduleBroadcast,
  useLineOAScheduledBroadcasts,
  useCancelScheduledBroadcast,
} from './useLineOABroadcast'

type SendMode = 'now' | 'schedule'

// Local (Asia/Bangkok / browser) "now + N minutes" as a datetime-local value
function localDateTimeValue(offsetMinutes = 0): string {
  const d = new Date(Date.now() + offsetMinutes * 60_000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

type MessageType = 'text' | 'image' | 'image_text'
type ComposeMode = 'basic' | 'rich' | 'video'

const MAX_TEXT_LENGTH = 5000
const MAX_IMAGE_SIZE = 10 * 1024 * 1024 // 10MB

// --- Phone preview (LINE chat mockup) ---
type RichPreview = { imageUrl: string | null; title: string; label: string }
type VideoPreview = { posterUrl: string | null }

function PhonePreview({
  botName,
  botPicture,
  text,
  imageSrc,
  rich,
  video,
}: {
  botName?: string
  botPicture?: string
  text: string
  imageSrc: string | null
  rich?: RichPreview
  video?: VideoPreview
}) {
  const hasContent =
    !!text.trim() || !!imageSrc || !!rich?.imageUrl || !!video?.posterUrl
  return (
    <div className="rounded-[18px] border border-black/10 overflow-hidden bg-[#8cabd9] shadow-sm">
      {/* Tiny chat header */}
      <div className="flex items-center gap-2 bg-[#7b9fd4] px-3 py-2">
        {botPicture ? (
          <img src={botPicture} alt="" className="w-6 h-6 rounded-full" />
        ) : (
          <span className="w-6 h-6 rounded-full bg-[#06C755]" />
        )}
        <span className="text-white text-xs font-medium truncate">{botName || 'Your OA'}</span>
      </div>
      {/* Chat body */}
      <div className="p-3 min-h-[300px] space-y-2">
        {!hasContent ? (
          <p className="text-white/70 text-[11px] text-center mt-16">
            Your message preview will appear here
          </p>
        ) : (
          <div className="flex items-start gap-2">
            {botPicture ? (
              <img src={botPicture} alt="" className="w-6 h-6 rounded-full shrink-0" />
            ) : (
              <span className="w-6 h-6 rounded-full bg-[#06C755] shrink-0" />
            )}
            <div className="max-w-[75%] space-y-1">
              {/* Video bubble: poster + play badge */}
              {video && video.posterUrl && (
                <div className="relative rounded-xl overflow-hidden">
                  <img src={video.posterUrl} alt="" className="w-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    </span>
                  </div>
                </div>
              )}
              {/* Rich (Flex) bubble: hero image + title + button */}
              {rich && rich.imageUrl && (
                <div className="bg-white rounded-xl overflow-hidden">
                  <img src={rich.imageUrl} alt="" className="w-full object-cover" />
                  <div className="p-2.5 space-y-2">
                    {rich.title.trim() && (
                      <p className="text-[13px] font-bold text-[#111] leading-snug">{rich.title}</p>
                    )}
                    <div className="rounded-lg bg-[#06C755] text-white text-[12px] font-medium text-center py-1.5">
                      {rich.label.trim() || 'View'}
                    </div>
                  </div>
                </div>
              )}
              {imageSrc && (
                <img src={imageSrc} alt="" className="rounded-xl w-full object-cover" />
              )}
              {text.trim() && (
                <div className="bg-white rounded-xl px-3 py-2 text-[13px] leading-snug text-[#111] whitespace-pre-wrap break-words">
                  {text}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// --- Row: left-aligned label stacked above its content ---
function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-sm font-semibold text-[#353a40] mb-2">{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

export function LineOABroadcastCompose() {
  const navigate = useNavigate()
  const { status, isLoading: isLoadingStatus } = useLineMessagingConfig()

  const { data: dashboard } = useLineOADashboard({ enabled: !!status?.connected })
  const { data: quota } = useLineOABroadcastQuota({ enabled: !!status?.connected })
  const sendBroadcast = useSendBroadcast()
  const scheduleBroadcast = useScheduleBroadcast()
  const { data: scheduledData } = useLineOAScheduledBroadcasts({ enabled: !!status?.connected })
  const cancelScheduled = useCancelScheduledBroadcast()

  const [composeMode, setComposeMode] = useState<ComposeMode>('basic')
  const [messageType, setMessageType] = useState<MessageType>('text')
  const [textContent, setTextContent] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  // Rich (Flex) + Video authoring state
  const [rich, setRichState] = useState<RichState>({
    imageFile: null, imageUrl: null, link: '', label: '', title: '',
  })
  const [video, setVideoState] = useState<VideoState>({
    videoFile: null, posterFile: null, posterUrl: null,
  })
  const setRich = useCallback((patch: Partial<RichState>) => setRichState((s) => ({ ...s, ...patch })), [])
  const setVideo = useCallback((patch: Partial<VideoState>) => setVideoState((s) => ({ ...s, ...patch })), [])

  // Effective message type sent to the backend.
  const effectiveMessageType: string =
    composeMode === 'rich' ? 'rich' : composeMode === 'video' ? 'video' : messageType
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [countdown, setCountdown] = useState(2)

  // Scheduler
  const [sendMode, setSendMode] = useState<SendMode>('now')
  const [scheduleAt, setScheduleAt] = useState('') // datetime-local value (Bangkok)
  const scheduledList = scheduledData?.scheduled ?? []
  const busy = sendBroadcast.isPending || scheduleBroadcast.isPending

  const switchMode = useCallback((m: ComposeMode) => {
    setComposeMode(m)
    if (m !== 'basic') setSendMode('now') // rich/video are send-now only
  }, [])

  const scheduleValid = useMemo(() => {
    if (sendMode !== 'schedule') return true
    if (!scheduleAt) return false
    return new Date(scheduleAt).getTime() > Date.now() + 30_000
  }, [sendMode, scheduleAt])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const followerCount = dashboard?.followers ?? 0

  useEffect(() => {
    if (!confirmOpen) {
      setCountdown(2)
      return
    }
    const id = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? (clearInterval(id), 0) : prev - 1))
    }, 1000)
    return () => clearInterval(id)
  }, [confirmOpen])

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview)
    }
  }, [imagePreview])

  // Revoke rich/video object URLs on unmount to avoid leaks.
  useEffect(() => {
    return () => {
      if (rich.imageUrl) URL.revokeObjectURL(rich.imageUrl)
      if (video.posterUrl) URL.revokeObjectURL(video.posterUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const needsText = messageType === 'text' || messageType === 'image_text'
  const needsImage = messageType === 'image' || messageType === 'image_text'

  const quotaInsufficient = useMemo(() => {
    if (!quota || quota.quota_type === 'none' || quota.remaining === null) return false
    return followerCount > quota.remaining
  }, [quota, followerCount])

  const richValid = !!rich.imageFile && /^https:\/\//.test(rich.link.trim())
  const videoValid = !!video.videoFile && !!video.posterFile

  const canSend = useMemo(() => {
    if (composeMode === 'rich') {
      if (!richValid) return false
    } else if (composeMode === 'video') {
      if (!videoValid) return false
    } else {
      if (needsText && !textContent.trim()) return false
      if (needsImage && !imageFile) return false
    }
    if (quotaInsufficient) return false
    if (!scheduleValid) return false
    return true
  }, [composeMode, richValid, videoValid, needsText, needsImage, textContent, imageFile, quotaInsufficient, scheduleValid])

  const validateAndSetImage = useCallback((file: File) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Image must be JPEG or PNG format.')
      return
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image must be less than 10MB.')
      return
    }
    if (imagePreview) URL.revokeObjectURL(imagePreview)
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
  }, [imagePreview])

  const clearImage = useCallback(() => {
    if (imagePreview) URL.revokeObjectURL(imagePreview)
    setImageFile(null)
    setImagePreview(null)
  }, [imagePreview])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) validateAndSetImage(file)
  }, [validateAndSetImage])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) validateAndSetImage(file)
    if (e.target) e.target.value = ''
  }, [validateAndSetImage])

  // Message-type toggles (Text / Photo — at least one must stay on)
  const toggleText = () => {
    if (needsText && needsImage) setMessageType('image')
    else if (!needsText) setMessageType(needsImage ? 'image_text' : 'text')
  }
  const toggleImage = () => {
    if (needsImage) {
      if (needsText) { setMessageType('text'); clearImage() }
    } else {
      setMessageType(needsText ? 'image_text' : 'image')
    }
  }

  // "2026-08-07T14:30" (datetime-local) → "2026-08-07 14:30:00" for the API
  const scheduledAtApi = scheduleAt ? scheduleAt.replace('T', ' ') + ':00' : ''

  const handleConfirmedSend = async () => {
    try {
      if (sendMode === 'schedule') {
        await scheduleBroadcast.mutateAsync({
          messageType,
          scheduledAt: scheduledAtApi,
          textContent: needsText ? textContent : undefined,
          imageFile: needsImage ? (imageFile ?? undefined) : undefined,
          recipientCount: followerCount,
        })
        toast.success('Broadcast scheduled.')
        navigate('/line-oa/broadcast')
      } else {
        await sendBroadcast.mutateAsync({
          messageType: effectiveMessageType,
          textContent:
            composeMode === 'basic' && needsText ? textContent : undefined,
          recipientCount: followerCount,
          media:
            composeMode === 'rich'
              ? {
                  richImageFile: rich.imageFile ?? undefined,
                  richLink: rich.link.trim(),
                  richLabel: rich.label.trim() || undefined,
                  richTitle: rich.title.trim() || undefined,
                }
              : composeMode === 'video'
                ? {
                    videoFile: video.videoFile ?? undefined,
                    previewFile: video.posterFile ?? undefined,
                  }
                : { imageFile: needsImage ? (imageFile ?? undefined) : undefined },
        })
        toast.success(`Broadcast sent to ${followerCount.toLocaleString()} followers.`)
        navigate('/line-oa/broadcast')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send broadcast.')
    } finally {
      setConfirmOpen(false)
    }
  }

  const fmtSchedule = (s: string) =>
    s ? new Date(s.replace(' ', 'T')).toLocaleString([], {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }) : ''

  if (isLoadingStatus) {
    return (
      <PageContainer>
        <div className="p-6 flex items-center justify-center min-h-[200px]">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    )
  }

  if (!status?.connected) {
    return (
      <PageContainer>
        <div className="p-6 space-y-4">
          <h2 className="text-2xl font-semibold">Broadcast</h2>
          <Card className="rounded-2xl border-0 max-w-lg">
            <CardContent className="p-6 space-y-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[#06C755]/10 flex items-center justify-center mx-auto">
                <Settings className="w-6 h-6 text-[#06C755]" />
              </div>
              <p className="font-medium">Connect LINE Messaging API</p>
              <Button asChild className="bg-[#06C755] hover:bg-[#06C755]/80">
                <Link to="/storefront/settings/line">Go to Settings</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    )
  }

  const remaining = quota?.quota_type === 'limited' ? quota.remaining : null

  return (
    <PageContainer>
      <div className="p-6 max-w-5xl">
        {/* Header toolbar */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Link to="/line-oa/broadcast" className="text-black/50 hover:text-[#353a40]">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h2 className="text-[1.6rem] font-semibold text-[#353a40]">Broadcast</h2>
          </div>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!canSend || busy}
            className="bg-[#06C755] hover:bg-[#06C755]/80 min-w-[120px]"
          >
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {sendMode === 'schedule' ? 'Schedule' : 'Send'}
          </Button>
        </div>
        <hr className="border-black/10 mb-6" />

        {/* Quota warning */}
        {quotaInsufficient && (
          <div className="flex items-start gap-3 p-4 rounded bg-red-50 text-red-700 mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Insufficient message quota</p>
              <p className="mt-1">
                This broadcast needs {followerCount.toLocaleString()} messages but only{' '}
                {(remaining ?? 0).toLocaleString()} remain this month.
              </p>
            </div>
          </div>
        )}

        <section className="flex flex-col lg:flex-row gap-6">
          {/* Left: form */}
          <div className="flex-1 min-w-0 space-y-6">
            <FormRow label="Recipients">
              <div className="flex items-center gap-3 rounded-lg border border-black/10 bg-[#06C755]/[0.04] px-3 py-2.5 max-w-[420px]">
                <span className="w-9 h-9 rounded-full bg-[#06C755]/10 flex items-center justify-center shrink-0">
                  <Users className="w-4.5 h-4.5 text-[#06C755]" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#353a40]">All friends</p>
                  <p className="text-xs text-black/50">
                    Reaches everyone who follows {status?.bot_display_name || 'your OA'}
                  </p>
                </div>
                <span className="text-right shrink-0">
                  <span className="block text-lg font-bold text-[#353a40] leading-none">
                    {followerCount.toLocaleString()}
                  </span>
                  <span className="text-[11px] text-black/45">friends</span>
                </span>
              </div>
            </FormRow>

            <hr className="border-black/10" />

            {/* Broadcast time — working scheduler (segmented toggle, no radios) */}
            <FormRow label="Broadcast time">
              <div className="inline-flex rounded-lg border border-black/10 bg-gray-100 p-1 gap-1">
                <button
                  type="button"
                  onClick={() => setSendMode('now')}
                  className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    sendMode === 'now'
                      ? 'bg-white text-[#06C755] shadow-sm'
                      : 'text-black/50 hover:text-[#353a40]'
                  }`}
                >
                  <Zap className="w-4 h-4" /> Send now
                </button>
                <button
                  type="button"
                  disabled={composeMode !== 'basic'}
                  onClick={() => {
                    setSendMode('schedule')
                    if (!scheduleAt) setScheduleAt(localDateTimeValue(30))
                  }}
                  title={composeMode !== 'basic' ? 'Rich and video messages send now only' : undefined}
                  className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    sendMode === 'schedule'
                      ? 'bg-white text-[#06C755] shadow-sm'
                      : 'text-black/50 hover:text-[#353a40]'
                  } ${composeMode !== 'basic' ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <CalendarClock className="w-4 h-4" /> Schedule for later
                </button>
              </div>
              {composeMode !== 'basic' && (
                <p className="mt-2 text-[11px] text-black/45">
                  Rich and video messages are sent immediately (scheduling supports text and photo).
                </p>
              )}

              {sendMode === 'schedule' && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    type="datetime-local"
                    value={scheduleAt}
                    min={localDateTimeValue(1)}
                    onChange={(e) => setScheduleAt(e.target.value)}
                    className="rounded-lg border border-black/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#06C755]/40"
                  />
                  <span className="text-[11px] text-black/45 border border-black/10 rounded-full px-2 py-1">
                    Asia/Bangkok · UTC+7
                  </span>
                  {scheduleAt && !scheduleValid && (
                    <span className="text-xs text-red-500">Pick a time in the future.</span>
                  )}
                </div>
              )}
            </FormRow>

            <hr className="border-black/10" />

            {/* Message-type mode selector */}
            <FormRow label="Message type">
              <div className="inline-flex flex-wrap rounded-lg border border-black/10 bg-gray-100 p-1 gap-1">
                {([
                  { id: 'basic', label: 'Text & Photo', Icon: Type },
                  { id: 'rich', label: 'Rich message', Icon: Sparkles },
                  { id: 'video', label: 'Video', Icon: Film },
                ] as { id: ComposeMode; label: string; Icon: typeof Type }[]).map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => switchMode(id)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                      composeMode === id
                        ? 'bg-white text-[#06C755] shadow-sm'
                        : 'text-black/50 hover:text-[#353a40]'
                    }`}
                  >
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>
            </FormRow>

            {/* Rich (Flex) message editor */}
            {composeMode === 'rich' && (
              <div className="rounded-[5px] border border-black/[0.15] bg-white overflow-hidden max-w-[640px]">
                <div className="border-b border-black/10 bg-[#f8f9fa] px-3 py-2 text-[11px] text-black/50">
                  An image that opens your link, with a title and a button
                </div>
                <RichEditor rich={rich} setRich={setRich} />
              </div>
            )}

            {/* Video message editor */}
            {composeMode === 'video' && (
              <div className="rounded-[5px] border border-black/[0.15] bg-white overflow-hidden max-w-[640px]">
                <div className="border-b border-black/10 bg-[#f8f9fa] px-3 py-2 text-[11px] text-black/50">
                  A video with a preview thumbnail
                </div>
                <VideoEditor video={video} setVideo={setVideo} />
              </div>
            )}

            {/* Message editor card (text / photo) */}
            {composeMode === 'basic' && (
            <div className="rounded-[5px] border border-black/[0.15] bg-white overflow-hidden max-w-[640px]">
              {/* Type toolbar */}
              <div className="flex items-center gap-1 border-b border-black/10 bg-[#f8f9fa] px-2 py-1.5">
                <button
                  type="button"
                  onClick={toggleText}
                  title="Text"
                  className={`w-9 h-9 rounded flex items-center justify-center transition-colors ${
                    needsText ? 'bg-[#06C755]/10 text-[#06C755]' : 'text-black/40 hover:bg-black/5'
                  }`}
                >
                  <Type className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={toggleImage}
                  title="Photo"
                  className={`w-9 h-9 rounded flex items-center justify-center transition-colors ${
                    needsImage ? 'bg-[#06C755]/10 text-[#06C755]' : 'text-black/40 hover:bg-black/5'
                  }`}
                >
                  <ImageIcon className="w-5 h-5" />
                </button>
                <span className="text-[11px] text-black/40 ml-2">Add text and/or a photo</span>
              </div>

              <div className="p-4 space-y-4">
                {/* Image */}
                {needsImage && (
                  <div>
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Broadcast" className="w-full rounded object-cover max-h-64" />
                        <button
                          type="button"
                          onClick={clearImage}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div
                        className={`border-2 border-dashed rounded p-8 text-center cursor-pointer transition-colors ${
                          isDragOver ? 'border-[#06C755] bg-[#06C755]/5' : 'border-black/15 hover:border-black/25'
                        }`}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={handleDrop}
                      >
                        <Upload className="w-8 h-8 text-black/30 mx-auto" />
                        <p className="text-sm text-black/50 mt-2">Click or drag an image here</p>
                        <p className="text-xs text-black/40 mt-1">JPEG or PNG, max 10MB</p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                )}

                {/* Text */}
                {needsText && (
                  <div>
                    <textarea
                      value={textContent}
                      onChange={(e) => setTextContent(e.target.value.slice(0, MAX_TEXT_LENGTH))}
                      placeholder="Enter text"
                      rows={6}
                      className="w-full resize-none rounded border border-black/15 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#06C755]/40 focus:border-transparent"
                    />
                    <div className="flex items-center justify-between mt-1">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs text-black/50 border border-black/15 rounded px-2 py-1 hover:bg-black/5"
                        onClick={() => setTextContent((t) => (t.length < MAX_TEXT_LENGTH ? t + '😊' : t))}
                      >
                        <Smile className="w-3.5 h-3.5" /> Emoji
                      </button>
                      <span className="text-xs text-black/40">
                        {textContent.length.toLocaleString()} / {MAX_TEXT_LENGTH.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Bottom send */}
            <div className="flex flex-col items-center pt-2 gap-1">
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={!canSend || busy}
                className="bg-[#06C755] hover:bg-[#06C755]/80 px-10 h-11 text-base"
              >
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                {sendMode === 'schedule' ? 'Schedule broadcast' : 'Send'}
              </Button>
              {sendMode === 'schedule' && scheduleValid && (
                <p className="text-xs text-black/50">Will send on {fmtSchedule(scheduledAtApi)}</p>
              )}
            </div>

            {/* Upcoming scheduled broadcasts */}
            {scheduledList.length > 0 && (
              <div className="rounded-[5px] border border-black/[0.125] bg-white p-4 max-w-[640px]">
                <h3 className="text-sm font-bold text-[#353a40] mb-3 flex items-center gap-1.5">
                  <CalendarClock className="w-4 h-4 text-[#06C755]" /> Scheduled ({scheduledList.length})
                </h3>
                <ul className="space-y-2">
                  {scheduledList.map((s) => (
                    <li key={s.name} className="flex items-center gap-3 border-t border-black/5 pt-2 first:border-0 first:pt-0">
                      <div className="w-9 h-9 rounded bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                        {s.image_url ? (
                          <img src={s.image_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Type className="w-4 h-4 text-black/30" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#353a40] truncate">
                          {s.content_preview || <span className="text-black/40 italic">Image message</span>}
                        </p>
                        <p className="text-[11px] text-[#06C755]">{fmtSchedule(s.scheduled_at)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => cancelScheduled.mutate(s.name)}
                        disabled={cancelScheduled.isPending}
                        className="text-black/40 hover:text-red-500 p-1.5 rounded hover:bg-black/5 shrink-0"
                        title="Cancel"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right: target estimate + preview */}
          <div className="lg:w-[260px] shrink-0 space-y-4">
            <div className="rounded-[5px] border border-black/[0.125] bg-white p-4 text-center">
              <p className="text-sm text-black/50 mb-3">Target estimate</p>
              <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center"
                   style={{ background: 'conic-gradient(#06C755 100%, #e9ecef 0)' }}>
                <div className="w-[76px] h-[76px] rounded-full bg-white flex items-center justify-center">
                  <span className="text-[1.6rem] font-bold text-[#353a40]">
                    100<span className="text-sm text-black/40">%</span>
                  </span>
                </div>
              </div>
              <p className="text-xs text-black/50 mt-3">
                Approx. <span className="font-semibold text-[#06C755]">{followerCount.toLocaleString()}</span> friends
              </p>
            </div>

            <PhonePreview
              botName={status?.bot_display_name}
              botPicture={status?.bot_picture_url}
              text={composeMode === 'basic' && needsText ? textContent : ''}
              imageSrc={composeMode === 'basic' && needsImage ? imagePreview : null}
              rich={
                composeMode === 'rich'
                  ? { imageUrl: rich.imageUrl, title: rich.title, label: rich.label }
                  : undefined
              }
              video={composeMode === 'video' ? { posterUrl: video.posterUrl } : undefined}
            />
          </div>
        </section>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {sendMode === 'schedule' ? 'Schedule this broadcast?' : 'Send broadcast?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {sendMode === 'schedule' ? (
                <>This will send your message to all {followerCount.toLocaleString()} friends of{' '}
                {status?.bot_display_name || 'your OA'} on <strong>{fmtSchedule(scheduledAtApi)}</strong> (Asia/Bangkok).
                You can cancel it before then.</>
              ) : (
                <>This sends your message to all {followerCount.toLocaleString()} friends of{' '}
                {status?.bot_display_name || 'your OA'}. This can’t be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button
              onClick={handleConfirmedSend}
              disabled={(sendMode === 'now' && countdown > 0) || busy}
              className="bg-[#06C755] hover:bg-[#06C755]/80"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {sendMode === 'schedule' ? 'Scheduling…' : 'Sending…'}</>
              ) : sendMode === 'schedule' ? (
                'Schedule'
              ) : countdown > 0 ? (
                `Send now (${countdown}s)`
              ) : (
                'Send now'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
